import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

const ADMIN_TOKEN_STASH = "cp_admin_token_stash";
const ADMIN_REFRESH_STASH = "cp_admin_refresh_stash";
const ACTIVE_WALLET_KEY = "cp_active_wallet"; // empty string = "All wallets" view
// Everything that remembers "what am I looking at". Impersonating parks the
// admin's own choices and starts clean; exiting hands them straight back, so
// nothing of the member's view can bleed into the admin session.
const VIEW_KEYS = [ACTIVE_WALLET_KEY, "portfolio_sel", "portal_lite", "show_community",
                   "wallets_focus", "wallets_page_size", "wallets_cols_v2", "org_view_mode"];
const VIEW_STASH = "cp_admin_view_stash";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = not authed
  const [bootstrapped, setBootstrapped] = useState(false);
  const [activeWalletId, setActiveWalletIdState] = useState(() => localStorage.getItem(ACTIVE_WALLET_KEY) || "");

  const setActiveWalletId = useCallback((id) => {
    const v = id || "";
    if (v) localStorage.setItem(ACTIVE_WALLET_KEY, v);
    else localStorage.removeItem(ACTIVE_WALLET_KEY);
    setActiveWalletIdState(v);
  }, []);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setBootstrapped(true);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("cp_token");
    if (token) {
      fetchMe();
    } else {
      setUser(null);
      setBootstrapped(true);
    }
  }, [fetchMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Admin (or trusted browser) → tokens come back immediately.
    if (data.access_token) {
      localStorage.setItem("cp_token", data.access_token);
      if (data.refresh_token) localStorage.setItem("cp_refresh", data.refresh_token);
      localStorage.removeItem(ADMIN_TOKEN_STASH);
      localStorage.removeItem(ADMIN_REFRESH_STASH);
      setUser(data.user);
      return { user: data.user };
    }
    // Member on a new browser → caller must collect the OTP and call verifyOtp.
    return {
      requiresOtp: !!data.requires_otp,
      otpSession: data.otp_session,
      emailMasked: data.email_masked,
      expiresInSeconds: data.expires_in_seconds,
    };
  };

  // Self-service sign-up from the login page. It does NOT sign them in: a login
  // only becomes a member when an admin attaches a wallet to it, so there is
  // nothing to enter yet. The caller shows them what happens next.
  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    return data;
  };

  const verifyOtp = async (otpSession, code) => {
    const { data } = await api.post("/auth/verify-otp", { otp_session: otpSession, code });
    localStorage.setItem("cp_token", data.access_token);
    if (data.refresh_token) localStorage.setItem("cp_refresh", data.refresh_token);
    localStorage.removeItem(ADMIN_TOKEN_STASH);
    localStorage.removeItem(ADMIN_REFRESH_STASH);
    setUser(data.user);
    return data.user;
  };

  const resendOtp = async (otpSession) => {
    const { data } = await api.post("/auth/resend-otp", { otp_session: otpSession });
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore — local cleanup still runs */ }
    localStorage.removeItem("cp_token");
    localStorage.removeItem("cp_refresh");
    localStorage.removeItem(ADMIN_TOKEN_STASH);
    localStorage.removeItem(ADMIN_REFRESH_STASH);
    localStorage.removeItem(ACTIVE_WALLET_KEY);
    setActiveWalletIdState("");
    setUser(null);
  };

  const startImpersonation = async (userId) => {
    // Stash the current (admin) tokens so we can restore them on exit.
    const currentToken = localStorage.getItem("cp_token");
    const currentRefresh = localStorage.getItem("cp_refresh");
    if (!currentToken) throw new Error("Not authenticated");
    const { data } = await api.post(`/admin/impersonate/${userId}`);
    localStorage.setItem(ADMIN_TOKEN_STASH, currentToken);
    if (currentRefresh) localStorage.setItem(ADMIN_REFRESH_STASH, currentRefresh);
    const mine = {};
    VIEW_KEYS.forEach((k) => {
      const v = localStorage.getItem(k);
      if (v != null) mine[k] = v;
      localStorage.removeItem(k);
    });
    localStorage.setItem(VIEW_STASH, JSON.stringify(mine));
    setActiveWalletIdState("");
    localStorage.setItem("cp_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const startPartnerView = async (partnerUserId) => {
    // Read-only "view as" for a white-label partner console. Same stash/restore
    // dance as member impersonation; the token itself refuses every write.
    const currentToken = localStorage.getItem("cp_token");
    const currentRefresh = localStorage.getItem("cp_refresh");
    if (!currentToken) throw new Error("Not authenticated");
    const { data } = await api.post(`/partners/logins/${partnerUserId}/view-as`);
    localStorage.setItem(ADMIN_TOKEN_STASH, currentToken);
    if (currentRefresh) localStorage.setItem(ADMIN_REFRESH_STASH, currentRefresh);
    const mine = {};
    VIEW_KEYS.forEach((k) => {
      const v = localStorage.getItem(k);
      if (v != null) mine[k] = v;
      localStorage.removeItem(k);
    });
    localStorage.setItem(VIEW_STASH, JSON.stringify(mine));
    setActiveWalletIdState("");
    localStorage.setItem("cp_token", data.access_token);
    return await fetchMe();
  };

  const stopImpersonation = async () => {
    // Call exit (best-effort — even if it fails we still restore the admin token).
    try { await api.post("/admin/impersonate/exit"); } catch {}
    const adminToken = localStorage.getItem(ADMIN_TOKEN_STASH);
    const adminRefresh = localStorage.getItem(ADMIN_REFRESH_STASH);
    localStorage.removeItem(ADMIN_TOKEN_STASH);
    localStorage.removeItem(ADMIN_REFRESH_STASH);
    // Drop whatever the member's session left behind, then put mine back.
    VIEW_KEYS.forEach((k) => localStorage.removeItem(k));
    let mine = {};
    try { mine = JSON.parse(localStorage.getItem(VIEW_STASH) || "{}"); } catch { mine = {}; }
    Object.entries(mine).forEach(([k, v]) => localStorage.setItem(k, v));
    localStorage.removeItem(VIEW_STASH);
    setActiveWalletIdState(mine[ACTIVE_WALLET_KEY] || "");
    if (adminToken) {
      localStorage.setItem("cp_token", adminToken);
      if (adminRefresh) localStorage.setItem("cp_refresh", adminRefresh);
      await fetchMe();
    } else {
      // No stash — fall back to a clean logout to be safe.
      await logout();
    }
  };

  const isImpersonating = !!(user && user.impersonated_by);

  return (
    <AuthContext.Provider value={{
      user, setUser, bootstrapped,
      login, register, verifyOtp, resendOtp, logout, refresh: fetchMe,
      isImpersonating,
      impersonatorEmail: user?.impersonator_email || null,
      impersonationReadonly: !!user?.impersonation_readonly,
      startImpersonation, startPartnerView, stopImpersonation,
      activeWalletId, setActiveWalletId,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
